import React, { useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import PlaceIcon from '@material-ui/icons/Room';
import RestoreIcon from '@material-ui/icons/Restore';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import StopIcon from '@material-ui/icons/Stop';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { PLACEMENT_ITEM_TYPES } from '../../../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles((theme) => ({
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  smallBtn: {
    textTransform: 'none',
    fontSize: 12,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    margin: theme.spacing(1.5, 0, 0.75),
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    marginBottom: theme.spacing(0.5),
    cursor: 'pointer',
    background: theme.palette.background.paper,
  },
  placing: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.action.hover,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: '1px solid rgba(0,0,0,0.15)',
    flexShrink: 0,
  },
  itemText: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  empty: {
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontSize: 13,
  },
}));

const parseNumeric = (val) => {
  if (!val) return 0;
  const cleaned = String(val).replace(/['"ftinm\s]/gi, '').replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
};

const parseRows = (text) => {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') inQuotes = !inQuotes;
      else if ((ch === ',' || ch === '\t') && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };
  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map((line, idx) => {
    const values = parseLine(line);
    return headers.reduce((row, header, i) => ({
      ...row,
      [header]: values[i] || '',
      _rowNum: idx + 1,
    }), {});
  });
  return { headers, rows };
};

const buildLabel = (itemType, details) => {
  const typeDef = PLACEMENT_ITEM_TYPES[itemType] || PLACEMENT_ITEM_TYPES.tree;
  return typeDef.buildLabel(details);
};

const createQueueItem = (itemType, details, sourceRow = null) => ({
  queueId: uuidv4(),
  itemType,
  label: buildLabel(itemType, details),
  details,
  status: 'unplaced',
  areaId: null,
  sourceRow,
});

const QueueMode = ({
  queue = [],
  onQueueChange,
  placingItemId,
  onStartPlacing,
  placementPaused,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importType, setImportType] = useState('tree');
  const [csvText, setCsvText] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [columnMap, setColumnMap] = useState({});
  const [manualRows, setManualRows] = useState([]);
  const [clearOpen, setClearOpen] = useState(false);

  const typeDef = PLACEMENT_ITEM_TYPES[importType] || PLACEMENT_ITEM_TYPES.tree;
  const unplaced = queue.filter(q => q.status === 'unplaced');
  const placed = queue.filter(q => q.status === 'placed');
  const skipped = queue.filter(q => q.status === 'skipped');

  const previewRows = useMemo(() => {
    if (!parsedData?.rows?.length) return [];
    return parsedData.rows.slice(0, 5).map(row => {
      const details = {};
      typeDef.fields.forEach(field => {
        const col = columnMap[field.key];
        if (col) details[field.key] = field.type === 'number' ? parseNumeric(row[col]) : row[col];
      });
      details._raw = { ...row };
      delete details._raw._rowNum;
      return { row, details, label: buildLabel(importType, details) };
    });
  }, [columnMap, importType, parsedData, typeDef.fields]);

  const autoMap = (headers, itemType) => {
    const next = {};
    const lower = headers.map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
    const def = PLACEMENT_ITEM_TYPES[itemType] || PLACEMENT_ITEM_TYPES.tree;
    def.fields.forEach(field => {
      const fieldKey = field.key.toLowerCase();
      const fieldLabel = field.label.toLowerCase().replace(/[^a-z0-9]/g, '');
      let idx = lower.findIndex(h => h === fieldKey || h === fieldLabel);
      if (idx === -1) idx = lower.findIndex(h => h.includes(fieldKey) || fieldKey.includes(h));
      if (idx === -1 && field.key === 'species') {
        idx = lower.findIndex(h => h.includes('common') || h.includes('scientific') || h.includes('name'));
      }
      if (idx >= 0) next[field.key] = headers[idx];
    });
    setColumnMap(next);
  };

  const handleParse = (text) => {
    const data = parseRows(text);
    setParsedData(data);
    autoMap(data.headers, importType);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result || '';
      setCsvText(text);
      handleParse(text);
    };
    reader.readAsText(file);
  };

  const importParsedRows = () => {
    if (!parsedData?.rows?.length) return;
    const nextItems = parsedData.rows.map(row => {
      const details = {};
      typeDef.fields.forEach(field => {
        const col = columnMap[field.key];
        if (col) details[field.key] = field.type === 'number' ? parseNumeric(row[col]) : row[col];
      });
      details._raw = { ...row };
      delete details._raw._rowNum;
      return createQueueItem(importType, details, row._rowNum);
    });
    onQueueChange([...queue, ...nextItems], 'Import queue items');
    setImportOpen(false);
    setParsedData(null);
    setCsvText('');
    setColumnMap({});
  };

  const importManualRows = () => {
    const expanded = [];
    manualRows.forEach(row => {
      const qty = Math.max(1, parseInt(row.qty, 10) || 1);
      const details = { ...row, qty };
      delete details.id;
      for (let i = 0; i < qty; i += 1) {
        const item = createQueueItem(importType, details);
        expanded.push({
          ...item,
          label: qty > 1 ? `${item.label} (${i + 1}/${qty})` : item.label,
        });
      }
    });
    onQueueChange([...queue, ...expanded], 'Build queue items');
    setManualRows([]);
    setImportOpen(false);
  };

  const updateItemStatus = (queueId, status) => {
    onQueueChange(queue.map(q => (q.queueId === queueId ? { ...q, status } : q)), 'Update queue item');
  };

  const removeItem = (queueId) => {
    onQueueChange(queue.filter(q => q.queueId !== queueId), 'Remove queue item');
    if (placingItemId === queueId) onStartPlacing(null);
  };

  const renderItem = (item, mode) => {
    const itemTypeDef = PLACEMENT_ITEM_TYPES[item.itemType] || PLACEMENT_ITEM_TYPES.tree;
    const isPlacing = placingItemId === item.queueId;
    return (
      <div
        key={item.queueId}
        className={`${classes.item} ${isPlacing ? classes.placing : ''}`}
        onClick={() => mode === 'unplaced' && onStartPlacing(item.queueId)}
      >
        <span className={classes.dot} style={{ background: itemTypeDef.color }} />
        <div className={classes.itemText}>
          <div className={classes.label}>{item.label || itemTypeDef.label}</div>
          <div className={classes.meta}>
            {itemTypeDef.label}{item.sourceRow ? ` · row ${item.sourceRow}` : ''}
          </div>
        </div>
        {isPlacing && (
          <Chip
            size="small"
            label={placementPaused ? 'Paused' : 'Click map'}
            color="primary"
            style={{ height: 22, fontSize: 11 }}
          />
        )}
        {mode === 'unplaced' && !isPlacing && (
          <Tooltip title="Place on map">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStartPlacing(item.queueId); }}>
              <PlaceIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {mode === 'unplaced' && (
          <Tooltip title="Skip">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); updateItemStatus(item.queueId, 'skipped'); }}>
              <SkipNextIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {mode === 'skipped' && (
          <Tooltip title="Restore">
            <IconButton size="small" onClick={() => updateItemStatus(item.queueId, 'unplaced')}>
              <RestoreIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete from queue">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeItem(item.queueId); }}>
            <DeleteIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  return (
    <Box>
      <div className={classes.actionRow}>
        {placingItemId && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            className={classes.smallBtn}
            startIcon={<StopIcon />}
            onClick={() => onStartPlacing(null)}
          >
            Stop
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          className={classes.smallBtn}
          startIcon={<CloudUploadIcon />}
          onClick={() => setImportOpen(true)}
        >
          Import / Build
        </Button>
        <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
          <Select value={importType} onChange={(e) => setImportType(e.target.value)}>
            {Object.entries(PLACEMENT_ITEM_TYPES).map(([key, def]) => (
              <MenuItem key={key} value={key}>{def.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Add one item">
          <IconButton
            size="small"
            onClick={() => onQueueChange([...queue, createQueueItem(importType, {})], 'Add queue item')}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
        {queue.length > 0 && (
          <Tooltip title="Clear queue">
            <IconButton size="small" onClick={() => setClearOpen(true)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {unplaced.length > 0 && <div className={classes.sectionTitle}>Unplaced ({unplaced.length})</div>}
      {unplaced.map(item => renderItem(item, 'unplaced'))}

      {placed.length > 0 && <div className={classes.sectionTitle}>Placed ({placed.length})</div>}
      {placed.map(item => renderItem(item, 'placed'))}

      {skipped.length > 0 && <div className={classes.sectionTitle}>Skipped ({skipped.length})</div>}
      {skipped.map(item => renderItem(item, 'skipped'))}

      {queue.length === 0 && (
        <div className={classes.empty}>Import a list or add items to place points on the map.</div>
      )}

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import {typeDef.label} list</DialogTitle>
        <DialogContent>
          <Box display="flex" gridGap={12} mb={2}>
            <FormControl size="small" variant="outlined" style={{ minWidth: 180 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={importType}
                onChange={(e) => {
                  setImportType(e.target.value);
                  if (parsedData?.headers?.length) autoMap(parsedData.headers, e.target.value);
                }}
              >
                {Object.entries(PLACEMENT_ITEM_TYPES).map(([key, def]) => (
                  <MenuItem key={key} value={key}>{def.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
              Upload CSV
            </Button>
            <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt" onChange={handleFileSelect} style={{ display: 'none' }} />
          </Box>

          <TextField
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            placeholder="Paste CSV or tab-delimited data with headers"
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
          />
          <Box mt={1} mb={2}>
            <Button
              variant="outlined"
              size="small"
              className={classes.smallBtn}
              disabled={!csvText.trim()}
              onClick={() => handleParse(csvText)}
            >
              Preview pasted data
            </Button>
          </Box>

          {parsedData?.headers?.length > 0 && (
            <Box mb={2}>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                Map columns
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap={8} mt={1}>
                {typeDef.fields.map(field => (
                  <FormControl key={field.key} variant="outlined" size="small">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={columnMap[field.key] || ''}
                      onChange={(e) => setColumnMap(prev => ({ ...prev, [field.key]: e.target.value }))}
                    >
                      <MenuItem value=""><em>Skip</em></MenuItem>
                      {parsedData.headers.map(header => (
                        <MenuItem key={header} value={header}>{header}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
            </Box>
          )}

          {previewRows.length > 0 && (
            <Box mb={2}>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                Preview first {previewRows.length} of {parsedData.rows.length}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Label</TableCell>
                    {typeDef.fields.filter(f => columnMap[f.key]).map(field => (
                      <TableCell key={field.key}>{field.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map(({ row, details, label }) => (
                    <TableRow key={row._rowNum}>
                      <TableCell>{row._rowNum}</TableCell>
                      <TableCell>{label}</TableCell>
                      {typeDef.fields.filter(f => columnMap[f.key]).map(field => (
                        <TableCell key={field.key}>{details[field.key]}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          <Typography variant="caption" style={{ fontWeight: 600 }}>
            Build manually
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name / Species</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {manualRows.map(row => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField value={row.species || row.material || ''} onChange={(e) => setManualRows(prev => prev.map(r => (r.id === row.id ? { ...r, species: e.target.value, material: e.target.value } : r)))} />
                  </TableCell>
                  <TableCell>
                    <TextField value={row.size || ''} onChange={(e) => setManualRows(prev => prev.map(r => (r.id === row.id ? { ...r, size: e.target.value } : r)))} />
                  </TableCell>
                  <TableCell>
                    <TextField type="number" value={row.qty || 1} onChange={(e) => setManualRows(prev => prev.map(r => (r.id === row.id ? { ...r, qty: e.target.value } : r)))} inputProps={{ min: 1 }} />
                  </TableCell>
                  <TableCell>
                    <TextField value={row.notes || ''} onChange={(e) => setManualRows(prev => prev.map(r => (r.id === row.id ? { ...r, notes: e.target.value } : r)))} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setManualRows(prev => prev.filter(r => r.id !== row.id))}>
                      <DeleteIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button
            size="small"
            startIcon={<AddIcon />}
            className={classes.smallBtn}
            onClick={() => setManualRows(prev => [...prev, { id: uuidv4(), qty: 1 }])}
          >
            Add row
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
          {parsedData?.rows?.length > 0 && (
            <Button color="primary" variant="contained" onClick={importParsedRows}>
              Import {parsedData.rows.length}
            </Button>
          )}
          {manualRows.length > 0 && (
            <Button color="primary" variant="contained" onClick={importManualRows}>
              Add {manualRows.reduce((sum, row) => sum + (parseInt(row.qty, 10) || 1), 0)}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={clearOpen} onClose={() => setClearOpen(false)}>
        <DialogTitle>Clear placement queue?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This removes all {queue.length} queue items from this takeoff.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearOpen(false)}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              onQueueChange([], 'Clear queue');
              onStartPlacing(null);
              setClearOpen(false);
            }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueueMode;
